"use client";

import { useMemo, useState } from "react";
import { withErrorBoundary } from "@sentry/nextjs";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import dynamic from "next/dist/shared/lib/dynamic";
import { usePathname } from "next/navigation";
import { FALLBACK_LOGO_IMAGE } from "@/constant";
import { IMAGE_POSTFIX_MOBILECARD, IMAGE_POSTFIX_WEBCARD } from "commonModule/globalConstant";
import { PropertyAttachmentType, PropertyTypeV2 } from "commonModule/services/common";
import { useDisclosure } from "utils/hooks/useDisclosure";
import { getCoverImg } from "utils/property-utils";
import DetailPageActionButtons from "../DetailPageActionButtons";
import DetailPageImage from "./DetailPageImage";
const AttachementsGalleryModal = dynamic(() => import( /* webpackChunkName: "AttachementsGalleryModal" */"../attachement-gallery/AttachementsGalleryModal"), {
  ssr: false
});
const PropertyAttachements = ({
  attachments,
  property_id,
  property_name,
  property
}: {
  attachments: PropertyAttachmentType[];
  property_id: string;
  property_name?: string;
  property: PropertyTypeV2;
}) => {
  const pathname = usePathname();
  const {
    isOpen: isGalleryOpen,
    onOpen,
    onClose
  } = useDisclosure();
  // Filter out video attachments from the list
  const filteredAttachments = attachments.filter(att => !att.is_video);
  const coverImg = getCoverImg(filteredAttachments, true);
  const isMultiUnitDetailPage = useMemo(() => pathname?.startsWith("/multi-unit-detail") || false, [pathname]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const shouldLoadMobileSlide = (slideIndex: number) => {
    if (filteredAttachments.length <= 2) {
      return true;
    }
    return Math.abs(slideIndex - currentSlide) <= 1;
  };
  const getMobileSrcSet = (src: string) => `${src}_${IMAGE_POSTFIX_MOBILECARD} 1x, ${src}_${IMAGE_POSTFIX_WEBCARD} 2x`;
  const renderDesktopView = () => {
    const secondImage = filteredAttachments[1] || {
      image_full_url: FALLBACK_LOGO_IMAGE
    };
    return <button className='relative grid h-full max-h-[480px] w-full cursor-pointer grid-cols-1 grid-rows-1 items-stretch gap-2 overflow-hidden disabled:cursor-default md:grid-cols-2 md:rounded-2xl' onClick={onOpen} disabled={filteredAttachments.length === 0} data-track-v2-event='CLICK_IMAGES' data-track-v2-props={JSON.stringify({
      propertyId: property_id
    })} data-sentry-component="renderDesktopView" data-sentry-source-file="index.tsx">
        <div className={`details-page-images relative col-span-1 aspect-[511/287] w-full overflow-hidden transition-all hover:brightness-75 hover:transition-all md:h-[480px]`}>
          <DetailPageImage src={coverImg || FALLBACK_LOGO_IMAGE} alt={property_name || "Vacation Rental Property Image"} loading='eager' fetchPriority='high' width={680} height={480} className='aspect-[680/480]' data-sentry-element="DetailPageImage" data-sentry-source-file="index.tsx" />
        </div>
        <div className={`details-page-images relative col-span-1 hidden overflow-hidden transition-all hover:brightness-75 hover:transition-all md:block md:h-[480px]`}>
          <DetailPageImage src={secondImage?.image_full_url} alt={`Vacation Rental Image 2`} loading='lazy' fetchPriority='low' decoding='async' width={680} height={480} className='aspect-[680/480]' data-sentry-element="DetailPageImage" data-sentry-source-file="index.tsx" />
          {filteredAttachments.length > 0 && <div className='pointer-events-none absolute bottom-5 right-4 flex h-8 items-center gap-2 rounded-md border-none bg-[#26262BA6] px-2 py-1 text-xs font-semibold text-white'>
              {`1 / ${filteredAttachments.length}`}
            </div>}
        </div>
      </button>;
  };
  const renderMobileView = () => <div className='relative' data-sentry-component="renderMobileView" data-sentry-source-file="index.tsx">
      <div className='absolute right-3 top-3 z-10 flex items-center gap-2'>
        <DetailPageActionButtons property={property} coverImg={coverImg || FALLBACK_LOGO_IMAGE} isMultiUnitDetailPage={isMultiUnitDetailPage} variant='overlay' data-sentry-element="DetailPageActionButtons" data-sentry-source-file="index.tsx" />
      </div>
      <div className='aspect-[412/321] w-full overflow-hidden' style={{
      transform: "translateZ(0)",
      WebkitTransform: "translateZ(0)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden"
    }}>
        <Splide className='h-full' options={{
        perPage: 1,
        gap: 0,
        padding: 0,
        arrows: false,
        pagination: false,
        height: "100%",
        perMove: 1,
        lazyLoad: "nearby"
      }} onMove={(_, currentSlideIndex) => {
        setCurrentSlide(currentSlideIndex);
      }} data-sentry-element="Splide" data-sentry-source-file="index.tsx">
          {filteredAttachments.length > 0 ? filteredAttachments.map((attachment, i) => <SplideSlide className='h-full' key={`attachment${i}`}>
                <button className='details-page-images h-full w-full' onClick={onOpen} data-track-v2-event='CLICK_IMAGES' data-track-v2-props={JSON.stringify({
            propertyId: property_id
          })}>
                  {shouldLoadMobileSlide(i) ? <DetailPageImage src={i === 0 ? coverImg : attachment.image_full_url || FALLBACK_LOGO_IMAGE} srcSet={getMobileSrcSet((i === 0 ? coverImg : attachment.image_full_url) || FALLBACK_LOGO_IMAGE)} sizes='100vw' postfix={IMAGE_POSTFIX_MOBILECARD} loading={i === currentSlide ? "eager" : "lazy"} fetchPriority={i === currentSlide ? "high" : "low"} width={412} height={321} className='aspect-[412/321]' alt={property_name || "Vacation Rental Property Image"} /> : <div aria-hidden='true' className='h-full w-full bg-gray-200' />}
                </button>
              </SplideSlide>) : <SplideSlide className='h-full'>
              <button className='details-page-images h-full w-full cursor-default'>
                <DetailPageImage src={FALLBACK_LOGO_IMAGE} loading='eager' width={412} height={321} className='aspect-[412/321]' alt={property_name || "Vacation Rental Property Image"} />
              </button>
            </SplideSlide>}
        </Splide>
      </div>

      {/* atachments gallery  */}
      {filteredAttachments.length > 0 && <div className='pointer-events-none absolute bottom-5 right-2 flex h-8 items-center gap-2 rounded-md border-none bg-[#26262BA6] px-2 py-1 text-xs font-semibold text-white lg2:bottom-4 lg2:right-4 lg2:text-base'>
          {/* <CameraIcon /> */}
          {`${currentSlide + 1} / ${filteredAttachments.length}`}
        </div>}
    </div>;
  return <>
      <div className='hidden md:block'>{renderDesktopView()}</div>
      <div className='md:hidden'>{renderMobileView()}</div>
      {/* there is no need to load all photos at initial render of detail page , that's why we lazy load this */}
      {isGalleryOpen && <AttachementsGalleryModal isGalleryOpen={isGalleryOpen} onClose={onClose} attachements={filteredAttachments} property={property} />}
    </>;
};
export default withErrorBoundary(PropertyAttachements, {
  beforeCapture: scope => {
    scope.setTag("ws_location", "Attachements");
  }
});